import { type UseMutationResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
	WorkflowActivateBody,
	WorkflowCreateBody,
	WorkflowRepairBody,
	WorkflowRepairReport,
	WorkflowReplacementBody,
	WorkflowResolverRuleCreateBody,
	WorkflowResolverRuleUpdateBody,
	WorkflowStatusCreateBody,
	WorkflowStatusUpdateBody,
	WorkflowTransitionCreateBody,
	WorkflowTransitionUpdateBody,
	WorkflowUpdateBody,
} from '@/domain/workflow-admin.types'
import { workflowAdminApi } from '@/services/workflow.service'

/**
 * React Query hooks for workflow administration. These hooks are the only
 * owner of workflow server state — a screen keeps form drafts, selections and
 * modal state locally and never mirrors a fetched collection.
 */

const ROOT = 'workflow-admin'

/** Every key is rooted under `ROOT`, so invalidation stays inside this feature. */
export const workflowAdminKeys = {
	all: [ROOT] as const,
	workflows: () => [ROOT, 'workflows'] as const,
	workflowList: (includeDeleted: boolean) => [ROOT, 'workflows', { includeDeleted }] as const,
	workflow: (id: string | null) => [ROOT, 'workflow', id] as const,
	statuses: (workflowId: string | null) => [ROOT, 'statuses', workflowId] as const,
	transitions: (workflowId: string | null) => [ROOT, 'transitions', workflowId] as const,
	resolverRules: () => [ROOT, 'resolver-rules'] as const,
	diagnostics: () => [ROOT, 'diagnostics'] as const,
	diagnosticsPage: (page: number, limit: number) => [ROOT, 'diagnostics', { page, limit }] as const,
}

// ─── Queries ────────────────────────────────────────────────────────────────

export const useWorkflows = (includeDeleted = false) =>
	useQuery({
		queryKey: workflowAdminKeys.workflowList(includeDeleted),
		queryFn: () => workflowAdminApi.listWorkflows(includeDeleted),
	})

export const useWorkflow = (id: string | null) =>
	useQuery({
		queryKey: workflowAdminKeys.workflow(id),
		queryFn: () => workflowAdminApi.getWorkflow(id as string),
		enabled: !!id,
	})

export const useWorkflowStatuses = (workflowId: string | null) =>
	useQuery({
		queryKey: workflowAdminKeys.statuses(workflowId),
		queryFn: () => workflowAdminApi.listStatuses(workflowId as string),
		enabled: !!workflowId,
	})

export const useWorkflowTransitions = (workflowId: string | null) =>
	useQuery({
		queryKey: workflowAdminKeys.transitions(workflowId),
		queryFn: () => workflowAdminApi.listTransitions(workflowId as string),
		enabled: !!workflowId,
	})

export const useResolverRules = () =>
	useQuery({ queryKey: workflowAdminKeys.resolverRules(), queryFn: workflowAdminApi.listResolverRules })

/**
 * Diagnostics is an explicit, page-addressed read. It never polls: a stale
 * report is corrected by the invalidations the administrative mutations fire.
 */
export const useWorkflowDiagnostics = (page = 1, limit = 20) =>
	useQuery({
		queryKey: workflowAdminKeys.diagnosticsPage(page, limit),
		queryFn: () => workflowAdminApi.listDiagnostics(page, limit),
	})

// ─── Invalidation helpers ───────────────────────────────────────────────────

/**
 * A small typed wrapper so each mutation below declares exactly which caches it
 * invalidates, instead of reaching for a blanket `invalidateQueries()`.
 */
function useInvalidator() {
	const qc = useQueryClient()
	return {
		workflows: () => void qc.invalidateQueries({ queryKey: workflowAdminKeys.workflows() }),
		workflow: (id: string) => void qc.invalidateQueries({ queryKey: workflowAdminKeys.workflow(id) }),
		statuses: (workflowId: string) => void qc.invalidateQueries({ queryKey: workflowAdminKeys.statuses(workflowId) }),
		transitions: (workflowId: string) =>
			void qc.invalidateQueries({ queryKey: workflowAdminKeys.transitions(workflowId) }),
		resolverRules: () => void qc.invalidateQueries({ queryKey: workflowAdminKeys.resolverRules() }),
		diagnostics: () => void qc.invalidateQueries({ queryKey: workflowAdminKeys.diagnostics() }),
		workOrders: () => {
			void qc.invalidateQueries({ queryKey: ['work-orders'] })
			void qc.invalidateQueries({ queryKey: ['work-order'] })
		},
	}
}

// ─── Workflow mutations ─────────────────────────────────────────────────────

export const useCreateWorkflow = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: (body: WorkflowCreateBody) => workflowAdminApi.createWorkflow(body),
		onSuccess: () => invalidate.workflows(),
	})
}

export const useUpdateWorkflow = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: WorkflowUpdateBody }) => workflowAdminApi.updateWorkflow(id, body),
		onSuccess: (_data, { id }) => {
			invalidate.workflows()
			invalidate.workflow(id)
		},
	})
}

/**
 * Activation can claim the tenant default in the same call, and the workflow's
 * usability changes what diagnostics reports — so both caches are invalidated.
 */
export const useActivateWorkflow = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: WorkflowActivateBody }) =>
			workflowAdminApi.activateWorkflow(id, body),
		onSuccess: (_data, { id }) => {
			invalidate.workflows()
			invalidate.workflow(id)
			invalidate.statuses(id)
			invalidate.diagnostics()
		},
	})
}

export const useDeactivateWorkflow = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body?: WorkflowReplacementBody }) =>
			workflowAdminApi.deactivateWorkflow(id, body),
		onSuccess: (_data, { id }) => {
			invalidate.workflows()
			invalidate.workflow(id)
			invalidate.statuses(id)
			invalidate.diagnostics()
		},
	})
}

export const useDeleteWorkflow = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body?: WorkflowReplacementBody }) =>
			workflowAdminApi.deleteWorkflow(id, body),
		onSuccess: (_data, { id }) => {
			invalidate.workflows()
			invalidate.workflow(id)
			invalidate.statuses(id)
			invalidate.diagnostics()
		},
	})
}

/**
 * The tenant default decides what a work order with no matching resolver rule
 * gets, so changing it changes the diagnostics report too. Nothing is applied
 * optimistically: a rejected change must leave the previous default displayed.
 */
export const useSetDefaultWorkflow = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: (id: string) => workflowAdminApi.setDefaultWorkflow(id),
		onSuccess: () => {
			invalidate.workflows()
			invalidate.diagnostics()
		},
	})
}

// ─── Status mutations ───────────────────────────────────────────────────────

export const useCreateWorkflowStatus = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: (body: WorkflowStatusCreateBody) => workflowAdminApi.createStatus(body),
		onSuccess: (_data, body) => {
			invalidate.statuses(body.workflowId)
			invalidate.workflow(body.workflowId)
			invalidate.diagnostics()
		},
	})
}

export const useUpdateWorkflowStatus = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: ({ statusId, body }: { statusId: string; workflowId: string; body: WorkflowStatusUpdateBody }) =>
			workflowAdminApi.updateStatus(statusId, body),
		onSuccess: (_data, { workflowId }) => {
			invalidate.statuses(workflowId)
			invalidate.workflow(workflowId)
			invalidate.diagnostics()
		},
	})
}

/**
 * The only way a status's position changes. Like the resolver-rule reorder it
 * returns the authoritative order, so the list is refetched rather than
 * reordered locally.
 */
export const useReorderWorkflowStatuses = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: ({ workflowId, statusIds }: { workflowId: string; statusIds: string[] }) =>
			workflowAdminApi.reorderStatuses(workflowId, statusIds),
		onSuccess: (_data, { workflowId }) => {
			invalidate.statuses(workflowId)
			invalidate.diagnostics()
			invalidate.workOrders()
		},
	})
}

export const useDeleteWorkflowStatus = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: ({ statusId }: { statusId: string; workflowId: string }) => workflowAdminApi.deleteStatus(statusId),
		onSuccess: (_data, { workflowId }) => {
			invalidate.statuses(workflowId)
			invalidate.transitions(workflowId)
			invalidate.workflow(workflowId)
			invalidate.diagnostics()
		},
	})
}

// ─── Transition mutations ───────────────────────────────────────────────────

export const useCreateWorkflowTransition = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: (body: WorkflowTransitionCreateBody) => workflowAdminApi.createTransition(body),
		onSuccess: (_data, body) => {
			invalidate.transitions(body.workflowId)
			invalidate.diagnostics()
		},
	})
}

export const useUpdateWorkflowTransition = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: ({
			transitionId,
			body,
		}: {
			transitionId: string
			workflowId: string
			body: WorkflowTransitionUpdateBody
		}) => workflowAdminApi.updateTransition(transitionId, body),
		onSuccess: (_data, { workflowId }) => {
			invalidate.transitions(workflowId)
			invalidate.diagnostics()
		},
	})
}

export const useDeleteWorkflowTransition = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: ({ transitionId }: { transitionId: string; workflowId: string }) =>
			workflowAdminApi.deleteTransition(transitionId),
		onSuccess: (_data, { workflowId }) => {
			invalidate.transitions(workflowId)
			invalidate.diagnostics()
		},
	})
}

// ─── Resolver-rule mutations ────────────────────────────────────────────────

export const useCreateResolverRule = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: (body: WorkflowResolverRuleCreateBody) => workflowAdminApi.createResolverRule(body),
		onSuccess: () => {
			invalidate.resolverRules()
			invalidate.diagnostics()
		},
	})
}

export const useUpdateResolverRule = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: ({ ruleId, body }: { ruleId: string; body: WorkflowResolverRuleUpdateBody }) =>
			workflowAdminApi.updateResolverRule(ruleId, body),
		onSuccess: () => {
			invalidate.resolverRules()
			invalidate.diagnostics()
		},
	})
}

export const useDeleteResolverRule = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: (ruleId: string) => workflowAdminApi.deleteResolverRule(ruleId),
		onSuccess: () => {
			invalidate.resolverRules()
			invalidate.diagnostics()
		},
	})
}

/** Reorder returns the authoritative order; the list is refetched rather than reordered locally. */
export const useReorderResolverRules = () => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: (ruleIds: string[]) => workflowAdminApi.reorderResolverRules(ruleIds),
		onSuccess: () => {
			invalidate.resolverRules()
			invalidate.diagnostics()
		},
	})
}

// ─── Repair ─────────────────────────────────────────────────────────────────

/**
 * Preview writes nothing, so it invalidates nothing. Apply changes work-order
 * associations, so it refreshes diagnostics and the Work Order/board caches.
 */
export const useRepairWorkOrders = (): UseMutationResult<WorkflowRepairReport, unknown, WorkflowRepairBody> => {
	const invalidate = useInvalidator()
	return useMutation({
		mutationFn: (body: WorkflowRepairBody) => workflowAdminApi.repair(body),
		onSuccess: (_report, body) => {
			if (body.dryRun) return
			invalidate.diagnostics()
			invalidate.workOrders()
		},
	})
}
